import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { isEmpty, toNumber, isNaN, get, size } from 'lodash';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  Icon,
  Card,
  Input,
  Textarea,
  FileUpload,
  Button,
  Skeleton,
} from '@repo/ui';
import { AppealStatus, AppealType } from '@repo/types';
import { dateUtil, numberUtil } from '@xtnd/web/shared';
import {
  Appeal,
  FileCategory,
  MatterInvoiceSummaryFile,
  UpdateAppealStatusPayload,
} from '../../types';
import {
  useGetInvoice,
  useMatterInvoiceSummaryFilesQuery,
  useUploadMatterInvoiceSummaryFiles,
} from '../../queries';

import { ExistingBARFilesSection } from '../common/FileAttachments';
import { AR_ATTACHMENT_ACCEPT, AR_ATTACHMENT_MAX_SIZE } from '../../constants/attachments';
import { AppealSummary, AppealSummarySkeleton } from './AppealSummary';

/**
 * Loading skeleton for the form
 */
export function AppealSubmittedSkeleton() {
  return (
    <div className="flex flex-col lg:flex-row gap-6 pt-4">
      {/* Left Side: Form Skeleton */}
      <div className="flex-1 space-y-6">
        {/* Financial Information Section Skeleton */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Skeleton className="w-10 h-10 rounded-lg mr-4" />
              <Skeleton className="h-6 w-40" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex flex-col gap-4">
              <Skeleton className="h-4 w-40 mb-2" />
              <div className="space-y-4">
                <div>
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div>
                  <Skeleton className="h-4 w-28 mb-2" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-start space-y-6">
              <Card className="rounded-xl p-5 shadow-sm">
                <Skeleton className="h-4 w-40 mb-2" />
                <Skeleton className="h-10 w-32 mb-2" />
                <Skeleton className="h-3 w-full mt-2" />
              </Card>

              <Card className="rounded-xl p-5 shadow-sm">
                <Skeleton className="h-4 w-36 mb-2" />
                <Skeleton className="h-10 w-32 mb-2" />
                <Skeleton className="h-3 w-full mt-2" />
              </Card>
            </div>
          </div>
        </Card>

        {/* Submission Details Section Skeleton */}
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <Skeleton className="w-10 h-10 rounded-lg mr-4" />
            <Skeleton className="h-6 w-40" />
          </div>
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-24 w-full mb-4" />
        </Card>
      </div>

      {/* Right Side: Invoice Summary Sidebar Skeleton */}
      <div className="lg:w-80 space-y-6">
        <AppealSummarySkeleton />

        {/* Deadlines & Eligibility Skeleton */}
        <Card className="grid grid-cols-1 gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-5 w-40" />
          </div>
          <div className="flex justify-between">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex justify-between">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex justify-between">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>
        </Card>

        {/* Action Card Skeleton */}
        <Card className="p-6 rounded-2xl shadow-xl sticky top-6">
          <Skeleton className="h-6 w-40 mb-4" />
          <div className="space-y-4">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
          <Skeleton className="h-10 w-full rounded-md mt-6" />
        </Card>
      </div>
    </div>
  );
}

/**
 * Props for the AppealModal component.
 *
 * @interface AppealModalProps
 */
interface MarkAppealSubmittedModalProps {
  /** Controls the visibility state of the modal */
  isOpen: boolean;
  /** Indicates whether an async operation is in progress */
  isLoading?: boolean;
  /** Maximum allowed amount for appeal approval (in cents) */
  maxAmount?: number;

  /** Invoice ID */
  invoiceId: string;

  /** Invoice Number */
  invoiceNumber: string;

  /** Current status of the appeal determining which form fields to display */
  selectedStatus: AppealStatus;
  basicInformation?: Partial<Appeal>;
  /** Callback invoked when the modal should close */
  onClose: () => void;
  /**
   * Async callback invoked on form submission.
   * Receives partial payload to update appeal status.
   *
   * @param payload - Partial appeal update data
   * @returns Promise that resolves when submission completes
   */
  onSubmit: (payload: Partial<UpdateAppealStatusPayload>) => Promise<void>;
}

/**
 * AppealModal Component
 *
 * A context-aware modal dialog for managing appeal workflows. Renders different
 * form fields based on the appeal status:
 * - APPEAL_SUBMITTED: Collects deadline and optional comment
 * - APPEAL_APPROVED: Collects amount (with validation) and optional comment
 *
 * Features:
 * - Smart amount input normalization (removes leading zeros, non-digits)
 * - Real-time validation with user-friendly error messages
 * - Max amount enforcement with formatted currency display
 * - Loading state management with disabled interactions
 * - Automatic form reset after successful submission
 *
 * @component
 * @example
 * ```tsx
 * <AppealModal
 *   isOpen={showModal}
 *   selectedStatus={APPEAL_STATUS.APPEAL_APPROVED}
 *   maxAmount={500000}
 *   isLoading={submitting}
 *   onClose={() => setShowModal(false)}
 *   onSubmit={handleAppealSubmit}
 * />
 * ```
 */
export const MarkAppealSubmittedModal: React.FC<MarkAppealSubmittedModalProps> = ({
  isOpen,
  invoiceId,
  invoiceNumber,
  isLoading = false,
  basicInformation,
  onClose,
  onSubmit,
}) => {
  const { data: invoice, isLoading: isInvoiceLoading } = useGetInvoice(invoiceId, {
    enabled: isOpen,
  });

  const { mutateAsync: uploadFiles } = useUploadMatterInvoiceSummaryFiles(invoiceId, 'AR Appeal', {
    isShowToast: false,
    category: FileCategory.APPEALS_SUPPORT,
  });

  const { data: existingFilesResponse } = useMatterInvoiceSummaryFilesQuery(invoiceId, {
    enabled: isOpen,
  });

  const attyDeadline = get(invoice, 'appeal.attyDeadline', '');
  const clientDeadline = get(invoice, 'appeal.deadline', '');
  const outstandingBalance = get(invoice, 'outstandingBalance', 0);

  const existingFiles = existingFilesResponse?.data || [];
  const appealType = get(invoice, 'appeal.appealType', AppealType.BACKLOG);

  const [formData, setFormData] = useState<{
    comment: string;
    appealedFees: string;
    appealedExpenses: string;
    files: File[];
    selectedExistingFiles: MatterInvoiceSummaryFile[];
  }>({
    appealedFees: '',
    appealedExpenses: '',
    comment: '',
    files: [],
    selectedExistingFiles: [],
  });

  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<{
    appealedFees: string;
    appealedExpenses: string;
    files: string;
  }>({
    appealedFees: '',
    appealedExpenses: '',

    files: '',
  });

  const actualBalance = useMemo(() => {
    return get(invoice, 'actualBalance', 0);
  }, [invoice]);

  const feesCut = get(invoice, 'appeal.feesCut', 0);
  const expensesCut = get(invoice, 'appeal.expensesCut', 0);

  useEffect(() => {
    if (invoice && isOpen) {
      setFormData({
        appealedFees: '0',
        appealedExpenses: '0',
        comment: '',
        files: [],
        selectedExistingFiles: [],
      });
    }
  }, [invoice, isOpen]);

  /**
   * Resets all form fields and error states to their initial values.
   * Called after successful submission or when modal is closed.
   *
   * @private
   */
  const resetForm = () => {
    setFormData({
      appealedFees: '',
      appealedExpenses: '',
      comment: '',
      files: [],
      selectedExistingFiles: [],
    });
    setErrors({ appealedFees: '', appealedExpenses: '', files: '' });
  };

  /**
   * Safely closes the modal only when not in loading state.
   * Prevents accidental closure during async operations.
   *
   * @private
   */
  const handleOnClose = () => {
    if (!isLoading) {
      onClose();
      resetForm();
    }
  };

  const title = useMemo(() => {
    return `Appeal Submitted - INV#${invoiceNumber}`;
  }, [invoiceNumber]);

  const handleAmountChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = numberUtil.normalizeNumericString(e.target.value);
    const AMOUNT_REGEX = /^\d*\.?\d{0,2}$/;
    if (!AMOUNT_REGEX.test(normalized)) {
      return;
    }

    const finalValue = normalized === '' ? '0' : normalized;
    const numericValue = Number(finalValue);

    setFormData(prev => ({ ...prev, [field]: finalValue }));
    setErrors(prev => ({ ...prev, [field]: '' }));

    if (field === 'appealedFees') {
      if (numericValue > feesCut) {
        setErrors(prev => ({
          ...prev,
          appealedFees: `The appealed fees cannot be greater than ${numberUtil.formatCurrency(feesCut)}`,
        }));
        return;
      }
    } else if (field === 'appealedExpenses') {
      if (numericValue > expensesCut) {
        setErrors(prev => ({
          ...prev,
          appealedExpenses: `The appealed expenses cannot be greater than ${numberUtil.formatCurrency(expensesCut)}`,
        }));
        return;
      }
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, comment: e.target.value }));
    setErrors(prev => ({ ...prev, comment: '' }));
  };

  const handleFilesChange = (files: File[]) => {
    setFormData(prev => ({ ...prev, files }));
    setErrors(prev => ({ ...prev, files: '' }));
  };

  const handleExistingFileSelection = (files: MatterInvoiceSummaryFile[]) => {
    setFormData(prev => ({ ...prev, selectedExistingFiles: files }));
    setErrors(prev => ({ ...prev, files: '' }));
  };

  const clientRefusedToPay = useMemo(() => {
    return get(invoice, 'appeal.feesCut', 0) + get(invoice, 'appeal.expensesCut', 0);
  }, [invoice]);

  const agreedToPayEstimation = useMemo(() => {
    return Math.max(0, actualBalance - clientRefusedToPay);
  }, [actualBalance, clientRefusedToPay]);

  const discounts = useMemo(() => {
    return (
      get(invoice, 'appeal.promptPayment', 0) +
      get(invoice, 'appeal.bulkDiscount', 0) +
      get(invoice, 'appeal.administrativeFee', 0)
    );
  }, [invoice]);

  const amountToBeAppealed = useMemo(() => {
    return Number(formData.appealedFees) + Number(formData.appealedExpenses);
  }, [formData.appealedExpenses, formData.appealedFees]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      let hasError = false;
      if (size(formData.files) === 0 && size(formData.selectedExistingFiles) === 0) {
        setErrors(prev => ({
          ...prev,
          files: 'At least one attachment is required',
        }));
        hasError = true;
      }

      if (Number(formData.appealedFees) === 0 && Number(formData.appealedExpenses) === 0) {
        setErrors(prev => ({
          ...prev,
          appealedFees: 'The appealed fees and expenses both cannot be 0',
        }));
        hasError = true;
      }

      if (hasError) {
        return;
      }

      try {
        let finalFiles = [...formData.selectedExistingFiles];

        if (size(formData.files) > 0) {
          const uploadResult = await uploadFiles({
            files: formData.files,
          });

          if (uploadResult.successFiles) {
            finalFiles = [...finalFiles, ...uploadResult.successFiles];
          }

          if (uploadResult.errorFiles && uploadResult.errorFiles.length > 0) {
            return;
          }
        }

        await onSubmit({
          nextStatus: AppealStatus.APPEAL_SUBMITTED,
          comment: formData.comment,
          appealedFees: Number(formData.appealedFees),
          appealedExpenses: Number(formData.appealedExpenses),
          files: finalFiles,
        });
        resetForm();
      } catch (err) {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = useMemo(() => {
    if (loading || isLoading) {
      return true;
    }

    const hasError = Object.values(errors).some(error => Boolean(error));
    if (hasError) {
      return true;
    }
  }, [loading, isLoading, errors]);

  const renderAppealSubmittedForm = () => {
    return (
      <div className="flex flex-col lg:flex-row gap-6 pt-4">
        {/* Left Side: Form */}
        <div className="flex-1 space-y-6">
          {/* Financial Information Section */}
          <Card className="p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-xtnd-light dark:bg-xtnd-dark-900 rounded-lg flex items-center justify-center mr-4">
                  <Icon
                    name="HiCurrencyDollar"
                    className="w-5 h-5 text-xtnd-blue dark:text-xtnd-blue-300"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                    Financial Information
                  </h2>
                  <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400 font-medium tracking-wide uppercase">
                    {appealType === AppealType.BACKLOG ? 'BACKLOG' : 'SUBSEQUENT'} MODE
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-2">
              <Card className="rounded-2xl shadow-sm !p-0 mb-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 bg-gray-200 dark:bg-gray-800 w-full py-3 px-4 rounded-t-lg rounded-b-none">
                    <Icon
                      name="HiLockClosed"
                      className="w-4 h-4 text-xtnd-dark-600 dark:text-xtnd-light-400"
                    />
                    <h4 className="text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-light uppercase tracking-wider">
                      Client Refused
                    </h4>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                    <div>
                      <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                        Fees Cut
                      </p>
                      <p className=" text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(Number(feesCut) || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                        Expenses Cut
                      </p>
                      <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(Number(expensesCut) || 0)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                      Client Refused to Pay
                    </p>
                    <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                      {numberUtil.formatCurrency(Number(clientRefusedToPay) || 0)}
                    </p>
                  </div>
                </div>
              </Card>

              <div className="flex flex-col mb-6">
                <Card className="overflow-hidden rounded-2xl border  shadow-lg !p-0">
                  <div className="flex items-center justify-between gap-3 bg-xtnd-light dark:bg-xtnd-blue/60 py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Icon name="HiPencil" className="h-4 w-4 " />
                      <h4 className="text-xs font-bold uppercase tracking-wider ">Appeal </h4>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-xtnd-dark-900 px-5 py-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-center justify-between">
                          <label
                            htmlFor="appealed-fees"
                            className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                          >
                            Appealed Fees <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs italic text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Max: {numberUtil.formatCurrency(Number(feesCut) || 0)}
                          </p>
                        </div>
                        <Input
                          id="appealed-fees"
                          type="number"
                          value={formData.appealedFees}
                          onChange={e => handleAmountChange('appealedFees', e)}
                          error={errors.appealedFees}
                          disabled={isLoading}
                          required
                          fullWidth
                          placeholder="0.00"
                          className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between">
                          <label
                            htmlFor="appealed-expenses"
                            className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                          >
                            Appealed Expenses <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs italic text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Max: {numberUtil.formatCurrency(Number(expensesCut) || 0)}
                          </p>
                        </div>
                        <Input
                          id="appealed-expenses"
                          type="number"
                          value={formData.appealedExpenses}
                          onChange={e => handleAmountChange('appealedExpenses', e)}
                          error={errors.appealedExpenses}
                          disabled={isLoading}
                          required
                          fullWidth
                          placeholder="0.00"
                          className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 border-t border-xtnd-light/60 dark:border-xtnd-blue/60  bg-xtnd-light/60 dark:bg-xtnd-blue/60 px-5 py-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-1 w-3/5">
                      <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full">
                        <Icon
                          name="HiInformationCircle"
                          className="h-4 w-4 text-xtnd-blue dark:text-xtnd-white"
                        />
                      </div>
                      <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-white">
                        The amount to be appealed to the client.
                      </p>
                    </div>

                    <div className="text-right text-xtnd-blue dark:text-xtnd-white">
                      <p className="text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
                        To be Appealed
                      </p>
                      <p className="mt-1 text-xl font-mono font-bold ">
                        {numberUtil.formatCurrency(amountToBeAppealed)}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="flex flex-col">
                <Card className="flex items-center justify-between gap-6 rounded-2xl border border-xtnd-light bg-white px-5 py-4 shadow-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-900">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-100 text-yellow-600 dark:bg-yellow-500/15 dark:text-yellow-300">
                      <Icon name="HiCalculator" className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                        Discounts
                      </h4>
                      <div className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
                        Pre-approved discounts applied to the invoice total.
                        <div className="flex flex-row gap-3 ">
                          <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Bulk Discount:{' '}
                            <span className="font-mono font-bold">
                              {numberUtil.formatCurrency(get(invoice, 'appeal.bulkDiscount', 0))}
                            </span>
                          </p>
                          <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Prompt Payment:{' '}
                            <span className="font-mono font-bold">
                              {numberUtil.formatCurrency(get(invoice, 'appeal.promptPayment', 0))}
                            </span>
                          </p>
                          <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Admin Fee:{' '}
                            <span className="font-mono font-bold">
                              {numberUtil.formatCurrency(
                                get(invoice, 'appeal.administrativeFee', 0)
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-sm font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                      {numberUtil.formatCurrency(discounts)}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-xtnd-dark-400 dark:text-xtnd-light-500">
                      Already Applied
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </Card>

          {/* Submission Details Section */}
          <Card
            className="p-6 shadow-sm opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                <Icon
                  name="HiDocumentText"
                  className="w-5 h-5 text-xtnd-dark-600 dark:text-xtnd-light-400"
                />
              </div>
              <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                Submission Details
              </h2>
            </div>

            <label
              htmlFor="comment"
              className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2"
            >
              Comment (Optional)
            </label>
            <Textarea
              id="comment"
              value={formData.comment}
              onChange={e => handleCommentChange(e)}
              disabled={isLoading}
              rows={4}
              fullWidth
              placeholder="Add any additional notes..."
            />
            <div className="mt-4 space-y-4">
              <label
                htmlFor="attachments"
                className="flex flex-col text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
              >
                <p>
                  Attachments <span className="text-red-500">*</span>
                </p>
                <span className="text-xs text-gray-400 dark:text-gray-200 italic font-normal">
                  Please include the original BAR and the appeal BAR.
                </span>
              </label>
              <FileUpload
                id="attachments"
                files={formData.files}
                isMultiple={true}
                accept={AR_ATTACHMENT_ACCEPT}
                maxSize={AR_ATTACHMENT_MAX_SIZE}
                disabled={isLoading}
                onChange={handleFilesChange}
                isDisplayUploadButton={false}
                maxFileNumber={12}
                error={errors.files}
                setError={(error: string) => setErrors(prev => ({ ...prev, files: error }))}
              />

              {/* Existing BAR Files Selection */}
              {existingFiles && existingFiles.length > 0 && (
                <ExistingBARFilesSection
                  existingFiles={existingFiles}
                  onSelectedFilesChange={handleExistingFileSelection}
                  columns={1}
                />
              )}
            </div>
          </Card>
        </div>

        {/* Right Side: Invoice Summary Sidebar */}
        <div className="lg:w-80 space-y-6">
          {basicInformation && <AppealSummary basicInformation={basicInformation} />}
          {/* Deadlines & Eligibility */}
          <Card
            className="grid grid-cols-1 gap-x-8 gap-y-3 animate-fadeInRight"
            style={{ animationDelay: '0.2s' }}
          >
            <h3 className="col-span-full text-lg font-bold mb-2 flex items-center text-xtnd-dark dark:text-xtnd-light">
              <Icon
                name="HiCalendar"
                className="w-5 h-5 mr-2 text-xtnd-dark dark:text-xtnd-light"
              />
              Deadlines & Eligibility
            </h3>

            <div className="text-sm">
              <div className="flex w-full items-start text-xtnd-dark dark:text-xtnd-light">
                <span className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Atty Response Deadline
                </span>
                <span className="ml-auto text-right text-amber-600 dark:text-amber-400 font-semibold">
                  {attyDeadline ? dateUtil.formatDate(attyDeadline, dateUtil.DateFormat.US) : '-'}
                </span>
              </div>
            </div>

            <div className="text-sm">
              <div className="flex w-full items-start text-xtnd-dark dark:text-xtnd-light">
                <span className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Client Appeal Deadline
                </span>
                <span className="ml-auto text-right font-semibold">
                  {clientDeadline
                    ? dateUtil.formatDate(clientDeadline, dateUtil.DateFormat.US)
                    : '-'}
                </span>
              </div>
            </div>

            <div className="text-sm">
              <div className="flex w-full items-start text-xtnd-dark dark:text-xtnd-light">
                <span className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Amount Eligible for Appeal
                </span>
                <span className={`ml-auto text-right font-mono font-semibold`}>
                  {numberUtil.formatCurrency(clientRefusedToPay)}
                </span>
              </div>
            </div>
          </Card>
          <Card
            className="!bg-transparent text-xtnd-dark dark:text-xtnd-light p-6 rounded-2xl shadow-xl sticky top-24 opacity-0 animate-fadeInRight"
            style={{ animationDelay: '0.15s' }}
          >
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[0.625rem] shadow-sm text-xtnd-dark dark:text-xtnd-light uppercase font-bold">
                    Agreed To Pay
                  </span>
                  <span className="text-xtnd-dark dark:text-xtnd-light font-mono font-bold text-lg">
                    {numberUtil.formatCurrency(agreedToPayEstimation)}
                  </span>
                </div>
                <Card className="!bg-amber-50 dark:!bg-amber-900/20 border !border-amber-100 dark:!border-amber-800 rounded-none p-2">
                  <h4 className="text-amber-800 dark:text-amber-300 text-sm font-bold mb-2 flex items-center">
                    <Icon name="HiInformationCircle" className="w-4 h-4 mr-2" />
                    Logic Summary
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-600 dark:text-amber-400">
                        Client Refused to Pay (Fees+Exp):
                      </span>
                      <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(clientRefusedToPay)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-600 dark:text-amber-400">Discounts:</span>
                      <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(discounts)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs border-t border-amber-200 dark:border-amber-400 pt-2 mt-2">
                      <span className="text-amber-600 dark:text-amber-400">All Reductions</span>
                      <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(clientRefusedToPay + discounts)}
                      </span>
                    </div>
                    <div className="pt-2 mt-2">
                      <p className="text-[10px] text-amber-700 dark:text-amber-300 leading-relaxed">
                        Agreed to Pay = Outstanding ({numberUtil.formatCurrency(outstandingBalance)}
                        ) - All Reductions (
                        {numberUtil.formatCurrency(clientRefusedToPay + discounts)})
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isDisabled}
              loading={isLoading || loading}
              fullWidth
              className="mt-6"
            >
              Submit Appeal
            </Button>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleOnClose}
      size="xxllg"
      className="!max-h-[95vh] overflow-y-auto"
      showCloseButton={false}
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">{title}</h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={() => handleOnClose()}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="pt-0 space-y-6">
        {isInvoiceLoading ? <AppealSubmittedSkeleton /> : renderAppealSubmittedForm()}
      </DialogBody>
    </Dialog>
  );
};
